import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  User, Users, Trash2, ChevronRight, ChevronLeft, MessageSquare, Check, X,
  Settings, ChevronDown, Clock, Mail, Send, FolderOpen, Download, Printer,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from '../components/Common/ConfirmDialog';
import SelectAllToggle from '../components/Common/SelectAllToggle';
import MoveCandidateModal from '../components/Employer/MoveCandidateModal';
import { interviewInvitationsApi } from '../services/interviewInvitationsApi';
import { candidateActionsApi } from '../services/candidateActionsApi';
import {
  candidateCommentsService,
  CandidateCommentSummary,
} from '../services/candidateCommentsService';
import {
  candidateEvaluationsService,
  CandidateEvaluationSummary,
  EvaluationValue,
} from '../services/candidateEvaluationsService';
import './JobApplicationsPage.scss';

// ── Interfaces ────────────────────────────────────────────────────────────────

interface JobApplication {
  id: number;
  job_id: number;
  user_id: number;
  cv_id: number | null;
  cover_letter_id: number | null;
  additional_message: string | null;
  status: 'pending' | 'accepted' | 'doubtful' | 'rejected';
  applied_at: string;
  reviewed_at: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  profile_photo: string | null;
  age?: number;
  city?: string;
  current_position?: string;
  company_name?: string;
  work_experience?: string;
  desired_salary?: string;
  desired_position?: string;
  job_title: string;
}

interface InvitedCandidate {
  id: number;
  candidate_id: number;
  cv_id: number | null;
  status: string;
  employer_evaluation: 'pending' | 'accepted' | 'doubtful' | 'rejected';
  sent_at: string;
  candidate_name: string;
  candidate_email?: string;
  candidate_phone?: string;
  candidate_photo?: string;
  job_title?: string;
  age?: number | null;
  city?: string;
  current_position?: string;
  company_name?: string;
  work_experience?: string;
  desired_salary?: string;
  desired_position?: string;
}

interface VisibleFields {
  experience: boolean;
  desiredJob: boolean;
  salary: boolean;
  coverLetter: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const EVAL_OPTIONS = [
  { value: 'pending',   label: 'Naujas',     labelPlural: 'Nauji' },
  { value: 'accepted',  label: 'Tinkamas',   labelPlural: 'Tinkami' },
  { value: 'doubtful',  label: 'Abejotinas', labelPlural: 'Abejotini' },
  { value: 'rejected',  label: 'Netinkamas', labelPlural: 'Netinkami' },
];

const DEFAULT_VISIBLE_FIELDS: VisibleFields = {
  experience: true,
  desiredJob: true,
  salary: true,
  coverLetter: true,
};

const FIELD_LABELS: Record<keyof VisibleFields, string> = {
  experience:  'Darbo patirtis',
  desiredJob:  'Ieškomas darbas',
  salary:      'Pageidaujamas atlyginimas',
  coverLetter: 'Motyvacinis laiškas',
};

function loadVisibleFields(jobId?: string): VisibleFields {
  try {
    const raw = localStorage.getItem(`appPageFields:${jobId || 'default'}`);
    if (raw) return { ...DEFAULT_VISIBLE_FIELDS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_VISIBLE_FIELDS;
}

function formatActivityDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('lt-LT');
  } catch {
    return iso;
  }
}

/**
 * Client-side fallback for work_experience data.
 *
 * The backend already parses the raw JSON via `deriveCvSummary`, but if the
 * server hasn't been restarted after that code was added it may still return
 * the raw JSON array string. This function handles both cases so the card
 * always shows a human-readable string.
 */
function resolveWorkInfo(
  company: string | undefined,
  position: string | undefined,
  workExp: string | undefined,
): { company: string; position: string; duration: string } {
  // Already parsed by the backend — plain text, not a JSON array
  if (!workExp || !workExp.trim().startsWith('[')) {
    return { company: company || '', position: position || '', duration: workExp || '' };
  }

  // Raw JSON path — parse client-side as a safety net
  try {
    const entries = JSON.parse(workExp) as Array<{
      companyName?: string;
      position?: string;
      startDate?: string;
      endDate?: string;
    }>;
    if (!Array.isArray(entries) || entries.length === 0) {
      return { company: company || '', position: position || '', duration: '' };
    }
    const sorted = [...entries].sort(
      (a, b) =>
        new Date(b.endDate || '9999-12-31').getTime() -
        new Date(a.endDate || '9999-12-31').getTime(),
    );
    const latest = sorted[0];
    let duration = '';
    if (latest.startDate) {
      const start = new Date(latest.startDate);
      const end = latest.endDate ? new Date(latest.endDate) : new Date();
      const totalMonths =
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth());
      const years = Math.floor(totalMonths / 12);
      const rem = totalMonths % 12;
      if (years > 0 && rem > 0) duration = `${years} metai ir ${rem} mėnesiai`;
      else if (years > 0) duration = `${years} metai`;
      else if (rem > 0) duration = `${rem} mėnesiai`;
    }
    return { company: latest.companyName || '', position: latest.position || '', duration };
  } catch {
    return { company: company || '', position: position || '', duration: '' };
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

const JobApplicationsPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // ── Data ───────────────────────────────────────────────────────────────────
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [invitations, setInvitations] = useState<InvitedCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobTitle, setJobTitle] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<JobApplication | null>(null);

  // ── Status filter (multi-select) ──────────────────────────────────────────
  const [activeStatuses, setActiveStatuses] = useState<string[]>(
    ['pending', 'accepted', 'doubtful', 'rejected'],
  );
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // ── Bulk selection ────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedAction, setSelectedAction] = useState('');
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // ── Per-card actions dropdown ─────────────────────────────────────────────
  const [openCardMenu, setOpenCardMenu] = useState<string | null>(null);

  // ── Field layout modal ────────────────────────────────────────────────────
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [visibleFields, setVisibleFields] = useState<VisibleFields>(
    () => loadVisibleFields(jobId),
  );

  // ── Comments ──────────────────────────────────────────────────────────────
  const [commentsByCandidate, setCommentsByCandidate] = useState<
    Record<string, CandidateCommentSummary>
  >({});
  const [commentTarget, setCommentTarget] = useState<
    | { candidateId: number; candidateName: string; initialText: string }
    | null
  >(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [moveTarget, setMoveTarget] = useState<{ cvId: number; candidateName: string } | null>(null);
  const [emailSending, setEmailSending] = useState(false);

  // ── Global per-candidate evaluation (Naujas/Tinkamas/Abejotinas/Netinkamas) ──
  // Source of truth shared with the CV detail page. Keyed by candidate user id.
  const [evalByCandidate, setEvalByCandidate] = useState<
    Record<string, EvaluationValue>
  >({});

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('auth_token');

      const appResponse = await fetch(`${API_BASE_URL}/job-applications/job/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (appResponse.ok) {
        const appResult = await appResponse.json();
        setApplications(appResult.data || []);
        if (appResult.data?.length > 0) setJobTitle(appResult.data[0].job_title);
      } else {
        const errorData = await appResponse.json();
        setError(errorData.message || 'Nepavyko užkrauti prašymų');
      }

      try {
        const invResponse = await fetch(`${API_BASE_URL}/job-invitations/job/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (invResponse.ok) {
          const invResult = await invResponse.json();
          const invData = invResult.data || [];
          setInvitations(invData);
          if (!jobTitle && invData.length > 0 && invData[0].job_title) {
            setJobTitle(invData[0].job_title);
          }
        }
      } catch { /* non-critical */ }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Įvyko klaida užkraunant duomenis');
    } finally {
      setLoading(false);
    }
  }, [jobId, API_BASE_URL, jobTitle]);

  useEffect(() => {
    if (!user || user.user_type !== 'employer') { navigate('/'); return; }
    loadData();
  }, [jobId, user, navigate, loadData]);

  useEffect(() => {
    const ids = new Set<number>();
    applications.forEach(a => ids.add(a.user_id));
    invitations.forEach(i => ids.add(i.candidate_id));
    if (ids.size === 0) { setCommentsByCandidate({}); setEvalByCandidate({}); return; }
    const idList = Array.from(ids);
    candidateCommentsService.bulk(idList)
      .then(setCommentsByCandidate)
      .catch(err => console.error('Failed to load candidate comments:', err));
    candidateEvaluationsService.bulk(idList)
      .then((map: Record<string, CandidateEvaluationSummary>) => {
        const next: Record<string, EvaluationValue> = {};
        Object.entries(map).forEach(([cid, row]) => { next[cid] = row.evaluation; });
        setEvalByCandidate(next);
      })
      .catch(err => console.error('Failed to load candidate evaluations:', err));
  }, [applications, invitations]);

  // Close card action menu on any outside click
  useEffect(() => {
    if (!openCardMenu) return;
    const handler = () => setOpenCardMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openCardMenu]);

  // Close status dropdown on outside click
  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [statusDropdownOpen]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const dedupedInvitations = useMemo(() => {
    if (invitations.length === 0) return invitations;
    const applicantIds = new Set(applications.map(a => a.user_id));
    return invitations.filter(i => !applicantIds.has(i.candidate_id));
  }, [applications, invitations]);

  const filteredApps = useMemo(
    () => applications.filter(a => activeStatuses.includes(evalByCandidate[String(a.user_id)] || 'pending')),
    [applications, activeStatuses, evalByCandidate],
  );
  const filteredInvs = useMemo(
    () => dedupedInvitations.filter(i => activeStatuses.includes(evalByCandidate[String(i.candidate_id)] || 'pending')),
    [dedupedInvitations, activeStatuses, evalByCandidate],
  );

  const allVisibleIds = useMemo(
    () => [
      ...filteredApps.map(a => `app-${a.id}`),
      ...filteredInvs.map(i => `inv-${i.id}`),
    ],
    [filteredApps, filteredInvs],
  );

  const countByStatus = (status: string) => {
    const appCount = applications.filter(a => (evalByCandidate[String(a.user_id)] || 'pending') === status).length;
    const invCount = dedupedInvitations.filter(i => (evalByCandidate[String(i.candidate_id)] || 'pending') === status).length;
    return appCount + invCount;
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const candidateHasComment = (id: number) => Boolean(commentsByCandidate[String(id)]);

  const getProfilePhotoUrl = (photo: string | null, name?: string): string => {
    if (!photo) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=23928B&color=fff&size=200`;
    }
    if (photo.startsWith('http')) return photo;
    return `${API_BASE_URL.replace('/api', '')}${photo.startsWith('/') ? photo : `/${photo}`}`;
  };

  // ── Status filter handlers ────────────────────────────────────────────────
  const toggleStatus = (status: string) => {
    setActiveStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status],
    );
  };

  // ── Bulk selection handlers ───────────────────────────────────────────────
  const toggleSelect = (id: string) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const handleToggleAll = () => {
    if (selectedIds.length === allVisibleIds.length && allVisibleIds.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds([...allVisibleIds]);
    }
  };

  const handleBulkAction = async () => {
    if (!selectedAction || selectedIds.length === 0) return;

    const appIds = selectedIds.filter(id => id.startsWith('app-')).map(id => parseInt(id.slice(4)));
    const invIds = selectedIds.filter(id => id.startsWith('inv-')).map(id => parseInt(id.slice(4)));

    switch (selectedAction) {
      case 'delete':
        setBulkDeleteConfirm(true);
        break;

      case 'download':
      case 'print': {
        const selectedApps = applications.filter(a => appIds.includes(a.id));
        const selectedInvs = invitations.filter(i => invIds.includes(i.id));
        const cvIds = [
          ...selectedApps.filter(a => a.cv_id).map(a => a.cv_id!),
          ...selectedInvs.filter(i => i.cv_id).map(i => i.cv_id!),
        ];
        if (cvIds.length === 0) {
          showToast('Pasirinkti kandidatai neturi CV', 'warning');
        } else {
          cvIds.forEach(cvId => window.open(`/cv/${cvId}?mode=employer`, '_blank'));
        }
        setSelectedAction('');
        break;
      }

      case 'reply': {
        const firstApp = applications.find(a => appIds.includes(a.id));
        const firstInv = invitations.find(i => invIds.includes(i.id));
        if (firstApp) handleReplyToCandidate(firstApp.user_id);
        else if (firstInv) handleReplyToCandidate(firstInv.candidate_id);
        setSelectedAction('');
        break;
      }
      case 'email': {
        const firstApp = applications.find(a => appIds.includes(a.id));
        const firstInv = invitations.find(i => invIds.includes(i.id));
        if (firstApp) handleEmailToMe(firstApp.cv_id, `${firstApp.first_name} ${firstApp.last_name}`.trim());
        else if (firstInv) handleEmailToMe(firstInv.cv_id, firstInv.candidate_name);
        setSelectedAction('');
        break;
      }
      case 'move': {
        const firstApp = applications.find(a => appIds.includes(a.id));
        const firstInv = invitations.find(i => invIds.includes(i.id));
        if (firstApp) handleMoveCv(firstApp.cv_id, `${firstApp.first_name} ${firstApp.last_name}`.trim());
        else if (firstInv) handleMoveCv(firstInv.cv_id, firstInv.candidate_name);
        setSelectedAction('');
        break;
      }
    }
  };

  const handleBulkDelete = async () => {
    const appIds = selectedIds.filter(id => id.startsWith('app-')).map(id => parseInt(id.slice(4)));
    if (appIds.length === 0) { setBulkDeleteConfirm(false); return; }
    const token = localStorage.getItem('auth_token');
    await Promise.all(
      appIds.map(id =>
        fetch(`${API_BASE_URL}/job-applications/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ),
    );
    setApplications(prev => prev.filter(a => !appIds.includes(a.id)));
    setSelectedIds(prev => prev.filter(id => !id.startsWith('app-') || !appIds.includes(parseInt(id.slice(4)))));
    setBulkDeleteConfirm(false);
    setSelectedAction('');
    showToast(`${appIds.length} kandidat${appIds.length === 1 ? 'as pašalintas' : 'ai pašalinti'}`, 'success');
  };

  // ── Single item handlers ──────────────────────────────────────────────────
  // Global per-candidate evaluation. The status is stored once per candidate
  // (not per application/invitation) so it stays in sync with the CV detail
  // page and every other job's applications page.
  const evalFor = (candidateUserId: number): EvaluationValue =>
    evalByCandidate[String(candidateUserId)] || 'pending';

  const handleEvalChange = async (candidateUserId: number, evaluation: string) => {
    const value = evaluation as EvaluationValue;
    const previous = evalByCandidate[String(candidateUserId)];
    setEvalByCandidate(prev => ({ ...prev, [String(candidateUserId)]: value }));
    try {
      await candidateEvaluationsService.save(candidateUserId, value);
    } catch {
      setEvalByCandidate(prev => {
        const next = { ...prev };
        if (previous) next[String(candidateUserId)] = previous;
        else delete next[String(candidateUserId)];
        return next;
      });
      showToast('Nepavyko atnaujinti būsenos', 'error');
    }
  };

  const handleDeleteApplication = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/job-applications/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        setApplications(prev => prev.filter(a => a.id !== deleteTarget.id));
      }
    } catch (err) {
      console.error('Error deleting application:', err);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleViewCV = (app: JobApplication) => {
    if (app.cv_id) {
      navigate(`/cv/${app.cv_id}?returnUrl=${encodeURIComponent(`/jobs/${jobId}/applications?mode=employer`)}`);
    } else {
      showToast('Kandidatas nepateikė CV', 'info');
    }
  };

  const handleViewCoverLetter = (message: string | null) => {
    if (message) {
      const modal = document.createElement('div');
      modal.className = 'cover-letter-modal';
      modal.innerHTML = `
        <div class="modal-overlay" onclick="this.closest('.cover-letter-modal').remove()">
          <div class="modal-content" onclick="event.stopPropagation()">
            <div class="modal-header">
              <h3>Motyvacinis laiškas</h3>
              <button class="modal-close" onclick="this.closest('.cover-letter-modal').remove()">×</button>
            </div>
            <div class="modal-body"><p>${message.replace(/\n/g, '<br>')}</p></div>
            <div class="modal-footer">
              <button class="btn-close" onclick="this.closest('.cover-letter-modal').remove()">Uždaryti</button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
    } else {
      showToast('Kandidatas nepateikė motyvacinio laiško', 'info');
    }
  };

  // ── Comment handlers ──────────────────────────────────────────────────────
  const openCommentModal = (candidateId: number, candidateName: string) => {
    const existing = commentsByCandidate[String(candidateId)]?.comment ?? '';
    setCommentTarget({ candidateId, candidateName, initialText: existing });
    setCommentDraft(existing);
  };

  const closeCommentModal = () => {
    if (commentSubmitting) return;
    setCommentTarget(null);
    setCommentDraft('');
  };

  const handleSaveComment = async () => {
    if (!commentTarget) return;
    const trimmed = commentDraft.trim();
    if (trimmed.length === 0) {
      try {
        setCommentSubmitting(true);
        await candidateCommentsService.remove(commentTarget.candidateId);
        setCommentsByCandidate(prev => {
          const next = { ...prev };
          delete next[String(commentTarget.candidateId)];
          return next;
        });
        showToast('Komentaras pašalintas', 'success');
        setCommentTarget(null);
        setCommentDraft('');
      } catch (err: any) {
        showToast(err?.message || 'Nepavyko pašalinti komentaro', 'error');
      } finally {
        setCommentSubmitting(false);
      }
      return;
    }
    if (trimmed.length > 5000) { showToast('Komentaras per ilgas (iki 5000 simbolių).', 'error'); return; }
    try {
      setCommentSubmitting(true);
      const { comment } = await candidateCommentsService.save(commentTarget.candidateId, trimmed);
      setCommentsByCandidate(prev => ({
        ...prev,
        [String(commentTarget.candidateId)]: {
          candidate_id: comment.candidate_id,
          comment: comment.comment,
          updated_at: comment.updated_at,
        },
      }));
      showToast('Komentaras išsaugotas', 'success');
      setCommentTarget(null);
      setCommentDraft('');
    } catch (err: any) {
      showToast(err?.message || 'Nepavyko išsaugoti komentaro', 'error');
    } finally {
      setCommentSubmitting(false);
    }
  };

  // ── Field layout helpers ──────────────────────────────────────────────────
  const toggleField = (field: keyof VisibleFields) => {
    setVisibleFields(prev => {
      const next = { ...prev, [field]: !prev[field] };
      try { localStorage.setItem(`appPageFields:${jobId || 'default'}`, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // ── Shared evaluation select ──────────────────────────────────────────────
  const renderEvalSelect = (value: string, onChange: (val: string) => void) => (
    <div className="candidate-card__evaluation">
      <label>Įvertinkite kandidatą:</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="evaluation-select"
      >
        {EVAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const handleReplyToCandidate = async (userId: number) => {
    setOpenCardMenu(null);
    try {
      const { invitation } = await interviewInvitationsApi.check(userId);
      if (invitation && (invitation.status === 'pending' || invitation.status === 'accepted')) {
        navigate(`/employer/pokalbiai/${invitation.id}`);
        return;
      }
      showToast('Nėra aktyvaus pokalbio su šiuo kandidatu', 'info');
    } catch {
      showToast('Nepavyko patikrinti pokalbių', 'error');
    }
  };

  const handleEmailToMe = async (cvId: number | null, candidateName: string) => {
    setOpenCardMenu(null);
    if (!cvId) {
      showToast('Kandidatas neturi CV', 'warning');
      return;
    }
    if (!user?.email) {
      showToast('Jūsų paskyroje nėra el. pašto adreso', 'error');
      return;
    }
    if (emailSending) return;
    try {
      setEmailSending(true);
      await candidateActionsApi.forwardByEmail(
        cvId,
        user.email,
        jobTitle ? `Kandidatas iš skelbimo „${jobTitle}“.` : `Kandidatas: ${candidateName}`,
      );
      showToast(`Kandidato CV išsiųstas į ${user.email}`, 'success');
    } catch (err: any) {
      showToast(err?.message || 'Nepavyko išsiųsti laiško', 'error');
    } finally {
      setEmailSending(false);
    }
  };

  const handleMoveCv = (cvId: number | null, candidateName: string) => {
    setOpenCardMenu(null);
    if (!cvId) {
      showToast('Kandidatas neturi CV', 'warning');
      return;
    }
    setMoveTarget({ cvId, candidateName });
  };

  // ── Per-card actions dropdown ─────────────────────────────────────────────
  const renderCardMenu = (cardId: string, cvId: number | null, userId: number, candidateName: string) => {
    if (openCardMenu !== cardId) return null;
    return (
      <div className="card-menu-dropdown" onClick={e => e.stopPropagation()}>
        <button type="button" onClick={() => handleReplyToCandidate(userId)}>
          <Mail size={14} />
          Atsakyti kandidatui
        </button>
        <button type="button" onClick={() => handleEmailToMe(cvId, candidateName)} disabled={emailSending}>
          <Send size={14} />
          Siųsti el. paštu
        </button>
        <button type="button" onClick={() => handleMoveCv(cvId, candidateName)}>
          <FolderOpen size={14} />
          Perkelti CV
        </button>
        <button type="button" onClick={() => {
          if (cvId) { window.open(`/cv/${cvId}?mode=employer`, '_blank'); }
          else { showToast('Kandidatas neturi CV', 'warning'); }
          setOpenCardMenu(null);
        }}>
          <Download size={14} />
          Atsisiųsti CV
        </button>
        <button type="button" onClick={() => {
          if (cvId) { window.open(`/cv/${cvId}?mode=employer&print=1`, '_blank'); }
          else { showToast('Kandidatas neturi CV', 'warning'); }
          setOpenCardMenu(null);
        }}>
          <Printer size={14} />
          Spausdinti CV
        </button>
      </div>
    );
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="job-applications-page">
        <div className="container">
          <div className="loading">
            <div className="loading-spinner" />
            <p>Kraunami kandidatai...</p>
          </div>
        </div>
      </div>
    );
  }

  const hasAny = applications.length > 0 || invitations.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="job-applications-page">
      <div className="container">

        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <Link to="/">Titulinis</Link>
          <ChevronRight size={14} />
          <Link to="/jobs?mode=employer">Skelbimai</Link>
          <ChevronRight size={14} />
          <span>{jobTitle || 'Kandidatai'}</span>
        </div>

        {/* Page title row */}
        <div className="page-title-row">
          <div className="page-title-row__left">
            <h1 className="page-title-row__title">{jobTitle || 'Kandidatai'}</h1>
            <button
              type="button"
              className="page-title-row__settings"
              onClick={() => navigate(`/jobs/${jobId}/edit?mode=employer`)}
              title="Redaguoti skelbimą"
            >
              <Settings size={16} />
            </button>
          </div>
          <button
            type="button"
            className="btn-back"
            onClick={() => navigate('/jobs?mode=employer')}
          >
            <ChevronLeft size={16} />
            Grįžti
          </button>
        </div>

        {error && <div className="alert alert--error">{error}</div>}

        {!hasAny ? (
          <div className="empty-state">
            <User size={48} />
            <h3>Kandidatų nerasta</h3>
            <p>Šiam darbo skelbimui dar niekas nėra pateikęs prašymo.</p>
          </div>
        ) : (
          <>
            {/* ── Toolbar ──────────────────────────────────────────────── */}
            <div className="candidates-toolbar">
              {/* Status multi-select filter */}
              <div className="status-filter-wrapper" ref={statusDropdownRef}>
                <button
                  type="button"
                  className={`status-filter-btn ${statusDropdownOpen ? 'open' : ''}`}
                  onClick={() => setStatusDropdownOpen(o => !o)}
                >
                  <span>Pasirinkti {activeStatuses.length} kriterijus</span>
                  <ChevronDown size={14} />
                </button>
                {statusDropdownOpen && (
                  <div className="status-filter-dropdown">
                    {EVAL_OPTIONS.map(opt => (
                      <label key={opt.value} className="status-filter-option">
                        <input
                          type="checkbox"
                          checked={activeStatuses.includes(opt.value)}
                          onChange={() => toggleStatus(opt.value)}
                        />
                        <span className={`eval-dot eval-dot--${opt.value}`} />
                        <span className="opt-label">{opt.labelPlural}</span>
                        <span className="opt-count">{countByStatus(opt.value)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Right cluster */}
              <div className="toolbar-right">
                <SelectAllToggle
                  selectedCount={selectedIds.length}
                  totalCount={allVisibleIds.length}
                  onToggle={handleToggleAll}
                />
                <div className="action-dropdown">
                  <select
                    className="action-select"
                    value={selectedAction}
                    onChange={e => setSelectedAction(e.target.value)}
                    disabled={selectedIds.length === 0}
                  >
                    <option value="">Pasirinkti veiksmą</option>
                    <option value="reply">Atsakyti kandidatams</option>
                    <option value="email">Siųsti el. paštu</option>
                    <option value="move">Perkelti CV</option>
                    <option value="download">Atsisiųsti CV</option>
                    <option value="print">Spausdinti CV</option>
                    <option value="delete">Trinti CV</option>
                  </select>
                  {selectedAction && selectedIds.length > 0 && (
                    <button className="apply-action-btn" onClick={handleBulkAction}>
                      Vykdyti
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="field-layout-btn"
                  onClick={() => setShowFieldModal(true)}
                  title="Redaguoti laukų išdėstymą"
                >
                  <Settings size={15} />
                  Redaguoti laukų išdėstymą
                </button>
              </div>
            </div>

            {/* ── Candidates grid ──────────────────────────────────────── */}
            {(filteredApps.length > 0 || filteredInvs.length > 0) ? (
              <div className="candidates-section">
                <div className="candidates-grid">

                  {/* Application cards */}
                  {filteredApps.map((app) => {
                    const hasComment = candidateHasComment(app.user_id);
                    const cardId = `app-${app.id}`;
                    const isSelected = selectedIds.includes(cardId);
                    return (
                      <div
                        key={cardId}
                        className={`candidate-card ${hasComment ? 'has-comment' : ''} ${isSelected ? 'selected' : ''} ${openCardMenu === cardId ? 'card-menu-open' : ''}`}
                      >
                        {/* Header */}
                        <div className="candidate-card__header candidate-card__header--app">
                          <div className="header-left">
                            <button
                              type="button"
                              className={`select-circle ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleSelect(cardId)}
                              title="Pasirinkti"
                            >
                              {isSelected && <Check size={10} strokeWidth={3} />}
                            </button>
                            <button
                              type="button"
                              className={`icon-btn icon-btn--comment ${hasComment ? 'is-active' : ''}`}
                              title={hasComment ? 'Redaguoti komentarą' : 'Rašyti komentarą'}
                              onClick={e => { e.stopPropagation(); openCommentModal(app.user_id, `${app.first_name} ${app.last_name}`); }}
                            >
                              <MessageSquare size={16} />
                            </button>
                          </div>
                          <div className="candidate-card__header-actions">
                            <button
                              type="button"
                              className={`icon-btn ${openCardMenu === cardId ? 'is-active' : ''}`}
                              title="Veiksmai"
                              onClick={e => { e.stopPropagation(); setOpenCardMenu(openCardMenu === cardId ? null : cardId); }}
                            >
                              <Settings size={16} />
                            </button>
                            <button
                              type="button"
                              className="icon-btn icon-btn--danger"
                              title="Ištrinti"
                              onClick={e => { e.stopPropagation(); setDeleteTarget(app); }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                        {renderCardMenu(cardId, app.cv_id, app.user_id, `${app.first_name} ${app.last_name}`.trim())}

                        {/* Photo */}
                        <div className="candidate-card__photo-container">
                          <div className="candidate-card__photo">
                            <img
                              src={getProfilePhotoUrl(app.profile_photo, `${app.first_name} ${app.last_name}`)}
                              alt={`${app.first_name} ${app.last_name}`}
                              onError={e => {
                                e.currentTarget.src = `https://ui-avatars.com/api/?name=${app.first_name}+${app.last_name}&background=23928B&color=fff&size=200`;
                              }}
                            />
                          </div>
                          {hasComment && (
                            <span className="candidate-card__comment-badge" title="Pridėtas komentaras">
                              <Check size={12} strokeWidth={3} />
                            </span>
                          )}
                        </div>

                        {/* Name */}
                        <h3 className="candidate-card__name">
                          {app.first_name} {app.last_name}
                          {app.age ? ` ${app.age} m.` : ''}
                          {app.city ? ` ${app.city}` : ''}
                        </h3>

                        {/* Info sections */}
                        {visibleFields.experience && (() => {
                          const w = resolveWorkInfo(app.company_name, app.current_position, app.work_experience);
                          if (!w.company && !w.position && !w.duration) return null;
                          return (
                            <div className="candidate-card__section">
                              <p className="section-title">Darbo patirtis (paskutinė pozicija)</p>
                              <p className="section-content">
                                {w.company && `${w.company}, `}{w.position}
                                {w.duration && ` (${w.duration})`}
                              </p>
                            </div>
                          );
                        })()}

                        {visibleFields.desiredJob && app.desired_position && (
                          <div className="candidate-card__section">
                            <p className="section-title">Ieškomas darbas</p>
                            <p className="section-content">{app.desired_position}</p>
                          </div>
                        )}

                        {visibleFields.salary && (
                          <div className="candidate-card__section">
                            <p className="section-title">Pageidaujamas atlyginimas:</p>
                            <p className="section-content salary">{app.desired_salary || '–'}</p>
                          </div>
                        )}

                        {/* Last activity */}
                        <div className="candidate-card__activity">
                          <Users size={13} />
                          <Clock size={13} />
                          <span>Paskutinis aktyvumas {formatActivityDate(app.applied_at)}</span>
                        </div>

                        {/* Evaluation */}
                        {renderEvalSelect(evalFor(app.user_id), val => handleEvalChange(app.user_id, val))}

                        {/* Cover letter — always shown, disabled when no letter */}
                        {visibleFields.coverLetter && (
                          <div className="candidate-card__letter">
                            <label>Motyvacinis laiškas:</label>
                            <button
                              type="button"
                              className={`btn-letter ${app.additional_message ? 'btn-letter--active' : 'btn-letter--empty'}`}
                              onClick={() => app.additional_message && handleViewCoverLetter(app.additional_message)}
                              disabled={!app.additional_message}
                            >
                              ŽIŪRĖTI
                            </button>
                          </div>
                        )}

                        {/* View CV */}
                        <button
                          type="button"
                          className="btn btn--view-cv"
                          onClick={() => handleViewCV(app)}
                          disabled={!app.cv_id}
                        >
                          ŽIŪRĖTI CV
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12,5 19,12 12,19" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}

                  {/* Invitation cards */}
                  {filteredInvs.map((inv) => {
                    const hasComment = candidateHasComment(inv.candidate_id);
                    const cardId = `inv-${inv.id}`;
                    const isSelected = selectedIds.includes(cardId);
                    return (
                      <div
                        key={cardId}
                        className={`candidate-card ${hasComment ? 'has-comment' : ''} ${isSelected ? 'selected' : ''} ${openCardMenu === cardId ? 'card-menu-open' : ''}`}
                      >
                        {/* Header */}
                        <div className="candidate-card__header candidate-card__header--invited">
                          <div className="header-left">
                            <button
                              type="button"
                              className={`select-circle ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleSelect(cardId)}
                              title="Pasirinkti"
                            >
                              {isSelected && <Check size={10} strokeWidth={3} />}
                            </button>
                            <button
                              type="button"
                              className={`icon-btn icon-btn--comment ${hasComment ? 'is-active' : ''}`}
                              title={hasComment ? 'Redaguoti komentarą' : 'Rašyti komentarą'}
                              onClick={e => { e.stopPropagation(); openCommentModal(inv.candidate_id, inv.candidate_name); }}
                            >
                              <MessageSquare size={16} />
                            </button>
                          </div>
                          <div className="candidate-card__header-actions">
                            <button
                              type="button"
                              className={`icon-btn ${openCardMenu === cardId ? 'is-active' : ''}`}
                              title="Veiksmai"
                              onClick={e => { e.stopPropagation(); setOpenCardMenu(openCardMenu === cardId ? null : cardId); }}
                            >
                              <Settings size={16} />
                            </button>
                          </div>
                        </div>
                        {renderCardMenu(cardId, inv.cv_id, inv.candidate_id, inv.candidate_name)}

                        {/* Photo */}
                        <div className="candidate-card__photo-container">
                          <div className="candidate-card__photo">
                            <img
                              src={getProfilePhotoUrl(inv.candidate_photo || null, inv.candidate_name)}
                              alt={inv.candidate_name}
                              onError={e => {
                                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(inv.candidate_name || 'K')}&background=23928B&color=fff&size=200`;
                              }}
                            />
                          </div>
                          {hasComment && (
                            <span className="candidate-card__comment-badge" title="Pridėtas komentaras">
                              <Check size={12} strokeWidth={3} />
                            </span>
                          )}
                        </div>

                        {/* Name */}
                        <h3 className="candidate-card__name">
                          {inv.candidate_name}
                          {inv.age ? ` ${inv.age} m.` : ''}
                          {inv.city ? ` ${inv.city}` : ''}
                        </h3>

                        {/* Info sections */}
                        {visibleFields.experience && (() => {
                          const w = resolveWorkInfo(inv.company_name, inv.current_position, inv.work_experience);
                          if (!w.company && !w.position && !w.duration) return null;
                          return (
                            <div className="candidate-card__section">
                              <p className="section-title">Darbo patirtis (paskutinė pozicija)</p>
                              <p className="section-content">
                                {w.company && `${w.company}, `}{w.position}
                                {w.duration && ` (${w.duration})`}
                              </p>
                            </div>
                          );
                        })()}

                        {visibleFields.desiredJob && inv.desired_position && (
                          <div className="candidate-card__section">
                            <p className="section-title">Ieškomas darbas</p>
                            <p className="section-content">{inv.desired_position}</p>
                          </div>
                        )}

                        {visibleFields.salary && (
                          <div className="candidate-card__section">
                            <p className="section-title">Pageidaujamas atlyginimas:</p>
                            <p className="section-content salary">{inv.desired_salary || '–'}</p>
                          </div>
                        )}

                        {/* Last activity */}
                        <div className="candidate-card__activity">
                          <Users size={13} />
                          <Clock size={13} />
                          <span>Paskutinis aktyvumas {formatActivityDate(inv.sent_at)}</span>
                        </div>

                        {/* Evaluation */}
                        {renderEvalSelect(evalFor(inv.candidate_id), val => handleEvalChange(inv.candidate_id, val))}

                        {/* Cover letter — always shown, invitations never have one */}
                        {visibleFields.coverLetter && (
                          <div className="candidate-card__letter">
                            <label>Motyvacinis laiškas:</label>
                            <button type="button" className="btn-letter btn-letter--empty" disabled>
                              ŽIŪRĖTI
                            </button>
                          </div>
                        )}

                        {/* View CV */}
                        <button
                          type="button"
                          className="btn btn--view-cv"
                          onClick={() => {
                            const returnPath = encodeURIComponent(`/jobs/${jobId}/applications?mode=employer`);
                            if (inv.cv_id) {
                              navigate(`/cv/${inv.cv_id}?returnUrl=${returnPath}`);
                            } else {
                              navigate(`/candidate/${inv.candidate_id}?mode=employer&returnUrl=${returnPath}`);
                            }
                          }}
                        >
                          ŽIŪRĖTI CV
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12,5 19,12 12,19" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}

                </div>
              </div>
            ) : (
              <div className="empty-state">
                <User size={48} />
                <h3>Nėra kandidatų su šia būsena</h3>
                <p>Pabandykite pasirinkti kitus kriterijus.</p>
              </div>
            )}
          </>
        )}

        {/* Single-item delete confirm */}
        {deleteTarget && (
          <ConfirmDialog
            message={`Ar tikrai norite pašalinti ${deleteTarget.first_name} ${deleteTarget.last_name} iš šio darbo skelbimo kandidatų?`}
            confirmText="Pašalinti"
            cancelText="Atšaukti"
            onConfirm={handleDeleteApplication}
            onCancel={() => setDeleteTarget(null)}
          />
        )}

        {/* Bulk delete confirm */}
        {bulkDeleteConfirm && (
          <ConfirmDialog
            message={`Ar tikrai norite pašalinti ${selectedIds.filter(id => id.startsWith('app-')).length} kandidatų? Šio veiksmo negalima atšaukti.`}
            confirmText="Pašalinti"
            cancelText="Atšaukti"
            onConfirm={handleBulkDelete}
            onCancel={() => setBulkDeleteConfirm(false)}
          />
        )}
      </div>

      {/* Comment modal */}
      {commentTarget && (
        <div className="comment-modal-overlay" onClick={closeCommentModal} role="presentation">
          <div
            className="comment-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="comment-modal-title"
          >
            <button type="button" className="comment-modal__close" onClick={closeCommentModal} disabled={commentSubmitting}>
              <X size={20} />
            </button>
            <div className="comment-modal__header">
              <MessageSquare size={20} />
              <h3 id="comment-modal-title">Rašyti komentarą</h3>
            </div>
            {commentTarget.candidateName && (
              <p className="comment-modal__subject">
                Kandidatas: <strong>{commentTarget.candidateName}</strong>
              </p>
            )}
            <textarea
              className="comment-modal__textarea"
              value={commentDraft}
              onChange={e => setCommentDraft(e.target.value)}
              placeholder="Įrašykite trumpą pastabą apie kandidatą — matysite tik jūs."
              maxLength={5000}
              autoFocus
              disabled={commentSubmitting}
            />
            <div className="comment-modal__footer">
              <span className="comment-modal__counter">{commentDraft.length}/5000</span>
              <button
                type="button"
                className="comment-modal__save-btn"
                onClick={handleSaveComment}
                disabled={commentSubmitting}
              >
                <Check size={18} />
                {commentSubmitting ? 'Saugoma...' : 'IŠSAUGOTI'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Field layout modal */}
      {showFieldModal && createPortal(
        <div className="field-modal-overlay" onClick={() => setShowFieldModal(false)} role="presentation">
          <div className="field-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="field-modal__header">
              <Settings size={18} />
              <h3>Rodyti laukus kortelėse</h3>
              <button type="button" className="field-modal__close" onClick={() => setShowFieldModal(false)}>
                <X size={20} />
              </button>
            </div>
            <p className="field-modal__hint">
              Pasirinkite, kurie laukai matomi kiekvieno kandidato kortelėje.
            </p>
            <div className="field-modal__options">
              {(Object.keys(DEFAULT_VISIBLE_FIELDS) as (keyof VisibleFields)[]).map(field => (
                <label key={field} className="field-modal__option">
                  <input
                    type="checkbox"
                    checked={visibleFields[field]}
                    onChange={() => toggleField(field)}
                  />
                  <span>{FIELD_LABELS[field]}</span>
                </label>
              ))}
            </div>
            <div className="field-modal__footer">
              <button type="button" className="btn-primary" onClick={() => setShowFieldModal(false)}>
                Išsaugoti
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {moveTarget && (
        <MoveCandidateModal
          cvId={moveTarget.cvId}
          candidateName={moveTarget.candidateName}
          onClose={() => setMoveTarget(null)}
          onMoved={(result) => {
            showToast(
              result === 'merged'
                ? 'Kandidatas jau buvo pasirinktame skelbime'
                : 'Kandidatas perkeltas',
              'success',
            );
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default JobApplicationsPage;
